import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../store/index';
import { fetchPost } from '../../../../helpers/fetching';
import DraggableDialog from '../DraggableDialog/DraggableDialog';
import { Button, Divider, Typography, makeStyles } from '@material-ui/core';

const IFU_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'pl', label: 'Polski' },
  { code: 'it', label: 'Italiano' },
  { code: 'el', label: 'Ελληνικά' },
  { code: 'sl', label: 'Slovenščina' },
  { code: 'no', label: 'Norsk' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'lt', label: 'Lietuvių' },
];

const openIfu = (code) => {
  window.open(`/bbox/ifu_${code}`, '_blank', 'noopener,noreferrer');
};

const useStyle = makeStyles((theme) => ({
  introText: {
    fontSize: theme.fonts.responsiveMedium,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  section: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
  },
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: theme.fonts.responsiveMediumBig,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.05rem',
  },
  sectionBody: {
    fontSize: theme.fonts.responsiveMedium,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  portalButton: {
    display: 'block',
    margin: '0 auto',
    backgroundColor: theme.palette.accents.dark,
    color: 'white',
    fontWeight: 'bold',
    height: '40px',
    fontSize: theme.fonts.responsiveMedium,
    padding: theme.spacing(1, 3),
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
    },
  },
  languageContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  languageButton: {
    width: '100px',
    height: '40px',
    fontSize: theme.fonts.responsiveMedium,
    fontWeight: 'bold',
    backgroundColor: theme.palette.accents.dark,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
    },
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  footerText: {
    fontSize: theme.fonts.responsiveSmall,
    textAlign: 'center',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
}));

const IfuLanguage = (props) => {
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();

  async function onOpenVideoPortalClick() {
    const response = await fetchPost('portal_launch');

    if (response.success && response.data.launch_url) {
      window.open(response?.data?.launch_url, '_blank', 'noopener,noreferrer');
      return;
    }

    dispatch(
      actions.setSnackbar({
        msg:
          response.message ||
          (response.msg && t(`api_messages.${response.msg}`, { defaultValue: response.msg })) ||
          `${t('support.error')}: Could not open video portal`,
        severity: 'error',
      }),
    );
  }

  const closeDialog = () => dispatch(actions.setActiveDialog(null));

  return (
    <DraggableDialog
      maxWidth='md'
      title={t('ifu_general.ifu_component_title')}
      open={props.open}
      actions={[
        {
          onClick: closeDialog,
          color: 'primary',
          label: t('Confirm.cancel'),
        },
      ]}
      onClose={closeDialog}>
      <Typography className={classes.introText}>{t('ifu_general.ifu_placeholder')}</Typography>

      <section className={classes.section}>
        <Typography className={classes.sectionTitle}>{t('ifu_general.open_learning_portal')}</Typography>
        <Typography className={classes.sectionBody}>{t('ifu_general.learning_portal_info')}</Typography>
        <span>
          <Button variant='contained' className={classes.portalButton} onClick={onOpenVideoPortalClick}>
            {t('ifu_general.open_learning_portal')}
          </Button>
        </span>
      </section>

      <Divider className={classes.divider} />

      <section className={classes.section}>
        <Typography className={classes.sectionTitle}>{t('ifu_general.open_ifu')}</Typography>
        <Typography className={classes.sectionBody}>{t('ifu_general.select_language')}</Typography>
        <div className={classes.languageContainer}>
          {IFU_LANGUAGES.map(({ code, label }) => (
            <Button
              key={code}
              variant='contained'
              className={classes.languageButton}
              onClick={() => openIfu(code)}>
              {label}
            </Button>
          ))}
        </div>
      </section>

      <Divider className={classes.divider} />

      <div>
        <Typography className={classes.footerText}>{t('ifu_general.ifu_search_helper')}</Typography>
      </div>
    </DraggableDialog>
  );
};

export default IfuLanguage;
